import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ArrowLeft, Calendar } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { posts } from "./aktuelles";

const BASE_URL = "https://schober-transportservice.at";

const MONATE: Record<string, string> = {
  Januar: "01", Februar: "02", "März": "03", April: "04", Mai: "05", Juni: "06",
  Juli: "07", August: "08", September: "09", Oktober: "10", November: "11", Dezember: "12",
};

function germanDateToIso(date: string): string {
  const m = date.match(/^(\d{1,2})\.\s*([A-Za-zÄÖÜäöü]+)\s+(\d{4})$/);
  if (!m) return date;
  const monat = MONATE[m[2]];
  if (!monat) return date;
  return `${m[3]}-${monat}-${m[1].padStart(2, "0")}`;
}

export const Route = createFileRoute("/aktuelles_/$slug")({
  loader: ({ params }) => {
    if (params.slug === "wie-belastend-entruempelung-sein-kann") {
      throw redirect({ to: "/aktuelles/$slug", params: { slug: "entruempelung-leicht-gemacht" }, statusCode: 301 });
    }
    const post = posts.find((p) => p.url === `/aktuelles/${params.slug}`);
    return { post: post ?? null, slug: params.slug };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post as (typeof posts)[number] | null;
    if (!post) {
      return {
        meta: [
          { title: "Beitrag nicht gefunden – Schober Transportservice" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }
    const url = `${BASE_URL}${post.url}`;
    const description = post.excerpt.replace(/\s+/g, " ").slice(0, 158);
    // Meta-Title separat vom H1: max. 65 Zeichen (SEO-Best-Practice).
    const metaTitle = (post.metaTitle ?? post.title).slice(0, 65);
    const iso = post.iso ?? germanDateToIso(post.date);
    return {
      meta: [
        { title: metaTitle },
        { name: "description", content: description },
        ...(post.keywords ? [{ name: "keywords", content: post.keywords }] : []),
        ...(post.noindex ? [{ name: "robots", content: "noindex, follow" }] : []),
        { property: "og:type", content: "article" },
        { property: "og:title", content: metaTitle },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        ...(post.image.startsWith("https://") ? [{ property: "og:image", content: post.image }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: metaTitle },
        { name: "twitter:description", content: description },
        ...(post.image.startsWith("https://") ? [{ name: "twitter:image", content: post.image }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                headline: post.title,
                description: description,
                image: BASE_URL + post.image,
                datePublished: iso,
                dateModified: iso,
                inLanguage: "de-AT",
                author: { "@type": "Organization", name: "Schober Transportservice" },
                publisher: {
                  "@type": "Organization",
                  name: "Schober Transportservice",
                  logo: { "@type": "ImageObject", url: BASE_URL + "/favicon.png" },
                },
                mainEntityOfPage: { "@type": "WebPage", "@id": url },
              },
              ...(post.faq && post.faq.length
                ? [
                    {
                      "@type": "FAQPage",
                      mainEntity: post.faq.map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: { "@type": "Answer", text: f.a },
                      })),
                    },
                  ]
                : []),
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
                  { "@type": "ListItem", position: 2, name: "Aktuelles", item: BASE_URL + "/aktuelles" },
                  { "@type": "ListItem", position: 3, name: post.title, item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: AktuellesPostPage,
});

function AktuellesPostPage() {
  const { slug } = Route.useParams();
  const post = posts.find((p) => p.url === `/aktuelles/${slug}`);

  if (!post) {
    return (
      <>
        <PageHero title="Nicht gefunden" subtitle="Der Beitrag konnte nicht gefunden werden." />
        <section className="container mx-auto px-4 py-20 text-center">
          <Link to="/aktuelles" className="inline-flex items-center gap-2 text-brand-orange font-bold">
            <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
          </Link>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero title="AKTUELLES" subtitle={post.title} />

      <section className="container mx-auto px-4 py-20">
        <div className="max-w-7xl mx-auto">
          <Link to="/aktuelles" className="inline-flex items-center gap-2 text-brand-orange font-bold mb-8">
            <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
          </Link>

          <div className="rounded-0 bg-background overflow-hidden mb-12">
            {post.image && (
              <div className="w-full h-[400px] overflow-hidden">
                <img src={post.image} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="py-8 md:py-12">
              <div className="flex items-center gap-2 text-sm uppercase tracking-wider text-brand-orange mb-4">
                <Calendar className="h-4 w-4" />
                {post.date}
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight text-brand-navy mb-8">
                {post.title}
              </h1>

              <div className="post_content prose prose-lg max-w-none text-muted-foreground" dangerouslySetInnerHTML={{ __html: post.content }} />

              {post.faq && post.faq.length > 0 && (
                <div className="mt-12">
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-brand-navy mb-6">
                    Häufige Fragen
                  </h2>
                  <div className="space-y-6">
                    {post.faq.map((f) => (
                      <div key={f.q}>
                        <h3 className="font-bold text-brand-navy mb-2">{f.q}</h3>
                        <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-12 rounded-2xl bg-brand-navy p-8 text-center">
                <h2 className="font-display text-2xl font-bold text-white mb-3">
                  kostenlose Beratung in 24 Stunden
                </h2>
                <p className="text-white/80 mb-6">
                  Umzug, Transport oder Entrümpelung in Wien, Niederösterreich und dem Burgenland – wir beraten Sie unverbindlich.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a
                    href="tel:+436802308700"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 font-bold text-white"
                  >
                    +43 680 230 87 00
                  </a>
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 font-bold text-white"
                  >
                    Anfrage senden
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
